const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Helper function for fetch requests
async function fetchAPI(url: string, options: RequestInit = {}): Promise<any> {
  const token = localStorage.getItem('token');
  const headers = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...options.headers,
  };

  const response = await fetch(url, { ...options, headers });

  if (!response.ok) {
    if (response.status === 404) {
      throw new Error('Not found');
    }
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return response.json();
}

export interface Certificate {
  id: number;
  uuid: string;
  userId: number;
  userName: string;
  userEmail: string;
  courseName: string;
  courseDescription?: string;
  companyId: number;
  issuedAt: string;
  expiresAt?: string;
  score?: string;
  verificationCode: string;
  isValid: boolean;
  isExpired: boolean;
  downloadUrl: string;
  verifyUrl: string;
  createdAt: string;
  updatedAt: string;
}

export interface CertificateVerification {
  valid: boolean;
  expired: boolean;
  certificate?: {
    userName: string;
    courseName: string;
    issuedAt: string;
    expiresAt?: string;
    score?: string;
    verificationCode: string;
  };
  message?: string;
}

export interface CertificateCount {
  count: number;
  valid: number;
  expired: number;
}

class CertificateService {
  /**
   * Get certificates fuer das Admin-Reporting — Company-Admins sehen alle,
   * normale User nur die eigenen. Fuer den User-Panel "Meine Zertifikate"
   * stattdessen `getMyCertificates()` nutzen, sonst sieht ein Admin auch
   * fremde Zertifikate dort (Issue #165).
   */
  async getCertificates(): Promise<Certificate[]> {
    const response = await fetchAPI(`${API_BASE_URL}/api/certificates`);
    return response['hydra:member'] || response['member'] || [];
  }

  /**
   * Get only the current user's own certificates (Issue #165).
   * Erzwingt userId-Filter unabhaengig von der Rolle.
   */
  async getMyCertificates(): Promise<Certificate[]> {
    const response = await fetchAPI(`${API_BASE_URL}/api/certificates?scope=me`);
    return response['hydra:member'] || response['member'] || [];
  }

  /**
   * Get a single certificate by UUID
   */
  async getCertificate(uuid: string): Promise<Certificate> {
    return fetchAPI(`${API_BASE_URL}/api/certificates/${uuid}`);
  }

  /**
   * Download certificate PDF
   * Opens the PDF in a new tab or triggers download
   */
  downloadCertificate(uuid: string): void {
    const token = localStorage.getItem('token');
    const downloadUrl = `${API_BASE_URL}/api/certificates/${uuid}/download`;

    // Create a temporary link with authorization
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.target = '_blank';

    // For authenticated download, we need to use fetch and create blob
    fetch(downloadUrl, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
      .then((response) => response.blob())
      .then((blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `certificate-${uuid}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      })
      .catch((error) => {
        console.error('Download failed:', error);
      });
  }

  /**
   * Verify a certificate by verification code (public endpoint)
   */
  async verifyCertificate(code: string): Promise<CertificateVerification> {
    // This is a public endpoint, no auth needed
    const response = await fetch(`${API_BASE_URL}/api/verify/${code}`);

    if (!response.ok) {
      if (response.status === 404) {
        return {
          valid: false,
          expired: false,
          message: 'Certificate not found',
        };
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  /**
   * Get certificate count for dashboard
   */
  async getCertificateCount(): Promise<CertificateCount> {
    return fetchAPI(`${API_BASE_URL}/api/certificates/count`);
  }

  /**
   * Generate the public verify URL for a certificate
   */
  getVerifyUrl(verificationCode: string): string {
    return `${window.location.origin}/verify/${verificationCode}`;
  }

  /**
   * Copy verify URL to clipboard
   */
  async copyVerifyUrl(verificationCode: string): Promise<boolean> {
    const url = this.getVerifyUrl(verificationCode);
    try {
      await navigator.clipboard.writeText(url);
      return true;
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      return false;
    }
  }

  /**
   * Format certificate date for display
   */
  formatDate(dateString: string, locale: string = 'de-DE'): string {
    const date = new Date(dateString);
    return date.toLocaleDateString(locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }

  /**
   * Check if a certificate is expiring soon (within N days)
   */
  isExpiringSoon(expiresAt: string | undefined, days: number = 30): boolean {
    if (!expiresAt) return false;

    const expiryDate = new Date(expiresAt);
    const now = new Date();
    const diffTime = expiryDate.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return diffDays > 0 && diffDays <= days;
  }

  /**
   * Get days until expiration
   */
  getDaysUntilExpiry(expiresAt: string | undefined): number | null {
    if (!expiresAt) return null;

    const expiryDate = new Date(expiresAt);
    const now = new Date();
    const diffTime = expiryDate.getTime() - now.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  }
}

export const certificateService = new CertificateService();
export default certificateService;
